import React, { Component } from "react";
import numeral from "numeral";
import { withStyles } from "@material-ui/core/styles";
import { Grid } from "@material-ui/core";
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import MeasureView from './MeasureView';
import { FlightLand, ExitToApp, Timeline, ReportProblem } from '@material-ui/icons';

const numberFormat = "0,0";

const styles = theme => ({
  row: {
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.background.default,
    },
  },
});

const PathTableCell = withStyles(theme => ({
  head: {
    color: theme.palette.common.black,
    width: '300px',
    fontSize: 18,
  },
  body: {
    fontSize: 14,
    width: '300px',
  },
}))(TableCell);

function getAncestorPath(node) {
  let parent = node.parent;
  const path = [];
  while (parent && parent.id !== 0) {
    path.push({ name: parent.name, itemId: parent.rid });
    parent = parent.parent;
  }
  return path;
}

function getChildPath(node) {
  const path = [];
  node.children.forEach(child => {
    path.push({ name: child.name, itemId: child.rid });
  });
  return path;
}

function convertTreeToList(root, pageId) {
  let stack = [], array = [], hashMap = {};
  stack.push(root);

  while (stack.length !== 0) {
    let node = stack.pop();
    if (node.rid === pageId) {
      visitNode(node, hashMap, array);
    } else {
      for (let i = node.children.length - 1; i >= 0; i--) {
        let child = node.children[i];
        child.parent = node;
        stack.push(child);
      }
    }
  }
  return array;
}

function visitNode(node, hashMap, array) {
  if (!hashMap[node.id]) {
    hashMap[node.id] = true;
    array.push(node);
  }
}

class ItemPaths extends Component {
  state = {}

  shouldComponentUpdate(nextProps, nextState) {
    return this.props.pageId !== nextProps.pageId;
  }

  render() {
    const { classes, paths } = this.props;

    const matches = convertTreeToList(paths.root, this.props.pageId);
    const incomingPaths = [];
    const outgoingPaths = [];
    matches.forEach(match => {
      const path = getAncestorPath(match).map(p => p.name).reverse();
      const isLanding = path.length === 0;
      let pathLabel = isLanding ? 'This page (landing)' : path.join(' > ');
      incomingPaths.push({ id: match.id, visits: match.sc, value: match.sv, valuePerVisit: match.sv / match.sc, potential: match.evp, path: pathLabel, exits: match.ec, isLanding: isLanding, exitValue: match.ev })
    });

    const totalLandingPageVisits = incomingPaths.reduce((sum, val) => val.isLanding ? sum + val.visits : sum, 0);

    const totalExits = incomingPaths.reduce((sum, val) => sum + val.exits, 0);
    const totalLeakage = incomingPaths.reduce((sum, val) => sum + val.exitValue, 0);

    matches.forEach(match => {
      const path = getChildPath(match).map(p => p.name);
      let pathLabel = path.length > 0 ? path.join(' > ') : 'This page';
      outgoingPaths.push({ id: match.id, visits: match.sc, value: match.sv, valuePerVisit: match.sv / match.sc, potential: match.evp, path: pathLabel })
    });

    return (
      <div className={classes.root}>
        <Grid container direction="row" spacing={8}>
          <Grid item md={12}>
            <Grid container spacing={8}>
              <Grid item xs={3} sm={3} md={3}>
                <MeasureView label="Landings" format="0,0" subheader="total visits landed on this page" value={totalLandingPageVisits} icon={(<FlightLand/>)}/>
              </Grid>
              <Grid item xs={3} sm={3} md={3}>
                <MeasureView label="Exits" format="0,0" subheader="total exits from this page" value={totalExits} icon={(<ExitToApp/>)} />
              </Grid>
              <Grid item xs={3} sm={3} md={3}>
                <MeasureView shouldDisplayWarning={v => v > 0} label="Paths with no value" format="0,0" subheader="total paths without engagement" value={incomingPaths.filter(p => p.value === 0).length} icon={(<Timeline/>)} />
              </Grid>
              <Grid item xs={3} sm={3} md={3}>
                <MeasureView shouldDisplayWarning={v => v > 0} label="Value leakage" format="0,0" subheader="total value that could be gained" value={totalLeakage} icon={(<ReportProblem/>)} />
              </Grid>
            </Grid>
          </Grid>
          <Grid item md={12}>
            <Table>
              <TableHead>
                <TableRow>
                  <PathTableCell>Incoming paths</PathTableCell>
                  <TableCell numeric>Visits</TableCell>
                  <TableCell numeric>Value</TableCell>
                  <TableCell numeric>Value / visit</TableCell>
                  <TableCell numeric>Value potential</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {incomingPaths.map(n => {
                  return (
                    <TableRow className={classes.row} key={n.id}>
                      <PathTableCell component="th" scope="row">
                        {n.path}
                      </PathTableCell>
                      <TableCell numeric>{numeral(n.visits).format(numberFormat)}</TableCell>
                      <TableCell numeric>{numeral(n.value).format(numberFormat)}</TableCell>
                      <TableCell numeric>{numeral(n.valuePerVisit).format(numberFormat)}</TableCell>
                      <TableCell numeric>{numeral(n.potential).format(numberFormat)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Grid>
          <Grid item md={12}>
            <Table>
              <TableHead>
                <TableRow>
                  <PathTableCell>Outgoing paths</PathTableCell>
                  <TableCell numeric>Visits</TableCell>
                  <TableCell numeric>Value</TableCell>
                  <TableCell numeric>Value / visit</TableCell>
                  <TableCell numeric>Value potential</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {outgoingPaths.map(n => {
                  return (
                    <TableRow className={classes.row} key={n.id}>
                      <PathTableCell component="th" scope="row">
                        {n.path}
                      </PathTableCell>
                      <TableCell numeric>{numeral(n.visits).format(numberFormat)}</TableCell>
                      <TableCell numeric>{numeral(n.value).format(numberFormat)}</TableCell>
                      <TableCell numeric>{numeral(n.valuePerVisit).format(numberFormat)}</TableCell>
                      <TableCell numeric>{numeral(n.potential).format(numberFormat)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Grid>
        </Grid>
      </div>
    );
  }
}

export default withStyles(styles)(ItemPaths);



// WEBPACK FOOTER //
// ./src/components/ItemPaths.js